/**
 * driver-pin — the initials on a driver's map pin (DISPATCH-02 / UI-SPEC §4).
 *
 * Pins are colored by availability in DriverMap (green / amber / red). The old per-driver
 * hash palette (driverColor) had no callers and went with the web restyle.
 *
 * driverInitials: first letter of first + last name, uppercased. Falls back to
 * first two letters of the name if only one word is present. Pure and unit-testable.
 */

/**
 * Returns the driver's initials (up to 2 characters) for display inside the
 * circular map pin (UI-SPEC §4: "driver initials, 12px semibold").
 *
 * Extraction logic:
 * - Split on whitespace to get name parts.
 * - Two or more parts: first letter of first part + first letter of last part.
 * - One part: first two letters of that part (e.g. "Duke" → "DU").
 * - Empty: "?" fallback.
 *
 * @param name  Driver's full name string.
 * @returns Uppercase initials string (1–2 characters).
 */
export function driverInitials(name: string): string {
  const trimmed = name.trim();
  if (trimmed.length === 0) return '?';

  const parts = trimmed.split(/\s+/);
  if (parts.length >= 2) {
    const first = parts[0]?.[0] ?? '';
    const last = parts[parts.length - 1]?.[0] ?? '';
    return (first + last).toUpperCase();
  }

  // Single-word name: use first two chars
  return trimmed.slice(0, 2).toUpperCase();
}
